import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withRouter } from 'react-router';
import queryString from 'query-string';

import {
  findHouseholdApplication,
  isApplicationCompleted,
  isApplicationPaid,
  getApplicationLimit,
} from 'common/householdApplication/applicationHelpers';

import isUserAgentMobileApp from 'common/util/isUserAgentMobileApp';
import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
} from 'client/helpers/sendDataToMobileApp';

import {
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
} from 'common/householdConstants';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_MAX_PRICE_TO_REQUEST_ONLINE_APP,
  LISTING_STATUS_LEASED,
  LISTING_STATUS_PENDING,
  LISTING_STATUS_NOT_AVAILABLE,
  LISTING_STATUS_NEEDS_REVIEW,
} from 'common/listingConstants';

import { isListingTenantScreening } from 'common/listingHelpers';

import {
  SR_API_ACTION_LISTING_REQUEST_ONLINE_APP,
  SR_API_ACTION_GET_HOUSEHOLDS_APPLYING_TO_LISTING,
  SR_API_ACTION_REQUEST_BUYERS_SIDE_APPLICATION,
  SR_API_ACTION_LISTING_WANTS_TO_APPLY,
} from 'api/actionTypes';

import log from 'client/lib/log';
import getHouseholdsApplyingToListing from 'client/actions/Listing/getHouseholdsApplyingToListing';

import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import withApiState from 'client/helpers/withApiState';
import HouseholdActions from 'client/actions/Household';
import ListingActions from 'client/actions/Listing';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrText from 'client/components/SrText';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

@withRouter
@withApiState((props) => ({
  requestingOnlineAppApiState: {
    action: SR_API_ACTION_LISTING_REQUEST_ONLINE_APP,
  },
  getHouseholdsApplyingToListingApiState: {
    action: SR_API_ACTION_GET_HOUSEHOLDS_APPLYING_TO_LISTING,
    queryId: props.listing ? props.listing.id : null,
  },
  requestingBuyerSideAppApiState: {
    action: SR_API_ACTION_REQUEST_BUYERS_SIDE_APPLICATION,
  },
}))
@branch((props) => {
  const branchParams = {
    householdApplications: ['householdApplications'],
    userAgent: ['analytics', 'userAgent'],
    currentUser: ['currentUser'],
  };
  if (props.listing && props.listing.id) {
    branchParams.householdsApplyingToListing = [
      'householdsApplyingToListingByListingId',
      props.listing.id,
    ];
  }
  return branchParams;
})
class SrApplicationButton extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    partner: PropTypes.object.isRequired,
    householdApplications: PropTypes.array,
    className: PropTypes.string,
    theme: PropTypes.string,
    requestingOnlineAppApiState: PropTypes.object.isRequired,
    householdsApplyingToListing: PropTypes.object,
    getHouseholdsApplyingToListingApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    userAgent: PropTypes.object.isRequired,
    requestingBuyerSideAppApiState: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      busy: false,
      requestingOnlineApp: false,
      requestingOnlineAppSuccess: false,
      isExternalApplicationModalOpen: false,
      requestingBuyerSideApp: false,
      buyerSideAppRequested: false,
    };
  }

  componentDidMount() {
    const { listing, dispatch } = this.props;
    dispatch(getHouseholdsApplyingToListing, {
      listingId: listing.id,
    });
  }

  _onStartApplication = () => {
    const { listing, currentUser, location, dispatch, partner } = this.props;

    const locationSearch = queryString.parse(location.search);

    if (
      partner &&
      partner.experienceConfiguration &&
      !!partner.experienceConfiguration.externalApplicationEnabled &&
      !!partner.experienceConfiguration.externalApplicationUrl
    ) {
      this._onToggleExternalApplicationModal();

      setTimeout(() => {
        global.location.href = partner.experienceConfiguration.externalApplicationUrl;
      }, 5000);

      dispatch(postWithState, {
        action: SR_API_ACTION_LISTING_WANTS_TO_APPLY,
        payload: {
          userId: currentUser.id,
          listingId: listing.id,
          wantsToApply: true,
        },
      }).catch((error) => {
        log.error(error);
      });
      return;
    }

    if (
      listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM &&
      listing.hasOnlineApp &&
      locationSearch.apply !== 'true' &&
      listing.price < LISTING_MAX_PRICE_TO_REQUEST_ONLINE_APP
    ) {
      this.setState({
        requestingOnlineApp: true,
      });
    } else if (listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM) {
      this._onRequestBuyerSideApp();
    } else {
      this.setState({
        busy: true,
      });
      dispatch(HouseholdActions.roommateApplicationStep, {
        listingId: listing.id,
      });
    }
  };

  _onButtonClick = () => {
    const { listing, householdApplications, history } = this.props;
    const householdApplication = findHouseholdApplication({
      householdApplications: householdApplications,
      category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
      listingId: listing.id,
    });

    const applicationPaid = householdApplication && isApplicationPaid({ householdApplication });

    const applicationCompleted =
      householdApplication && isApplicationCompleted({ householdApplication });

    const isNonSunrooomListingAppPaid =
      listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      applicationPaid;

    const isSunroomListingAppCompleted =
      listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      applicationCompleted;

    const hasCompletedApp = isNonSunrooomListingAppPaid || isSunroomListingAppCompleted;

    if (!hasCompletedApp) {
      this._onStartApplication();
    } else {
      history.push('/profile');
    }
  };

  _onRequestOnlineApp = () => {
    const { listing, dispatch } = this.props;
    dispatch(ListingActions.requestOnlineApp, {
      listingId: listing.id,
    }).then(() => {
      this.setState({
        requestingOnlineApp: false,
        requestingOnlineAppSuccess: true,
      });
    });
  };

  _onRequestBuyerSideApp = () => {
    const { currentUser, listing, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_REQUEST_BUYERS_SIDE_APPLICATION,
      payload: {
        userId: currentUser.id,
        listingId: listing.id,
      },
    }).then(() => {
      this.setState({
        requestingBuyerSideApp: true,
        buyerSideAppRequested: true,
      });
    });
  };

  _onCloseRequestBuyerSideAppModal = () => {
    this.setState({
      requestingBuyerSideApp: false,
      buyerSideAppRequested: true,
    });
  };

  _onCloseRequestOnlineAppModals = () => {
    this.setState({
      requestingOnlineApp: false,
      requestingOnlineAppSuccess: false,
    });
  };

  _onToggleExternalApplicationModal = () => {
    this.setState({
      isExternalApplicationModalOpen: !this.state.isExternalApplicationModalOpen,
    });
  };

  _onOpenExternalApplicationLink = () => {
    const { listing, userAgent } = this.props;
    const isMobileApp = isUserAgentMobileApp(userAgent);

    event.preventDefault();

    if (isMobileApp) {
      sendDataToMobileApp({
        actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
        url: listing.externalApplicationLink,
      });
    } else {
      global.open(listing.externalApplicationLink, '_blank');
    }

    this._onToggleExternalApplicationModal();
  };

  render() {
    const {
      listing,
      partner,
      householdApplications,
      requestingOnlineAppApiState,
      householdsApplyingToListing,
      getHouseholdsApplyingToListingApiState,
      requestingBuyerSideAppApiState,
      className,
      theme,
    } = this.props;

    const {
      busy,
      requestingOnlineApp,
      requestingOnlineAppSuccess,
      isExternalApplicationModalOpen,
      requestingBuyerSideApp,
      buyerSideAppRequested,
    } = this.state;

    const householdApplication = findHouseholdApplication({
      householdApplications: householdApplications,
      category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
      listingId: listing.id,
    });

    const applicationStarted =
      householdApplication && householdApplication.status === HOUSEHOLD_APPLICATION_STATUS_STARTED;

    const applicationCompleted =
      householdApplication && isApplicationCompleted({ householdApplication });

    const applicationPaid = householdApplication && isApplicationPaid({ householdApplication });

    const isOnlineApp = householdApplication && householdApplication.isOnlineApp;

    const householdsPaidAppButtonBlocked =
      listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      householdsApplyingToListing &&
      householdsApplyingToListing.tentativeQualifiedAndProcessing.excludingCurrentHousehold >=
        getApplicationLimit(listing); // eslint-disable-line max-len

    const householdsAcceptedAppButtonBlocked =
      listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      householdsApplyingToListing &&
      householdsApplyingToListing.accepted.excludingCurrentHousehold >= 1;

    const getHouseholdsApplyingToListingBusy =
      listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      getHouseholdsApplyingToListingApiState.inProgress;

    const isNonSunrooomListingAppPaid =
      listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      applicationPaid;

    const isSunroomListingAppCompleted =
      listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
      !isListingTenantScreening(listing) &&
      applicationCompleted;

    const buttonDisabled =
      getHouseholdsApplyingToListingBusy ||
      householdsPaidAppButtonBlocked ||
      householdsAcceptedAppButtonBlocked ||
      listing.status !== LISTING_STATUS_ACTIVE ||
      isOnlineApp ||
      requestingOnlineAppApiState.inProgress ||
      requestingBuyerSideAppApiState.inProgress ||
      buyerSideAppRequested ||
      busy;

    let buttonText = '';
    if (
      busy ||
      requestingOnlineAppApiState.inProgress ||
      getHouseholdsApplyingToListingApiState.inProgress ||
      requestingBuyerSideAppApiState.inProgress
    ) {
      buttonText = 'please wait...';
    } else if (isOnlineApp || buyerSideAppRequested) {
      buttonText = 'application requested';
    } else if (
      listing.status === LISTING_STATUS_LEASED ||
      listing.status === LISTING_STATUS_PENDING
    ) {
      buttonText = 'leased';
    } else if (
      listing.status === LISTING_STATUS_NOT_AVAILABLE ||
      listing.status === LISTING_STATUS_NEEDS_REVIEW ||
      householdsAcceptedAppButtonBlocked
    ) {
      buttonText = 'not available';
    } else if (isNonSunrooomListingAppPaid || isSunroomListingAppCompleted) {
      buttonText = 'view application';
    } else if (applicationStarted) {
      buttonText = 'continue application';
    } else {
      buttonText = 'apply now';
    }

    return (
      <div>
        {requestingOnlineApp && (
          <SrSimpleMessageModal
            iconUrl="/images/house-illustration2x.png"
            headline="Request the Application!"
            message={
              'Click “request application” below to notify the property manager that ' +
              'you want to apply. We’ll work with to help you apply successfully!'
            }
            buttonText={
              requestingOnlineAppApiState.inProgress ? 'please wait...' : 'request application'
            }
            subLabelText="no thanks, I don’t want to apply"
            onSubLabelClick={this._onCloseRequestOnlineAppModals}
            maxWidth={375}
            onClose={this._onCloseRequestOnlineAppModals}
            onButtonClick={this._onRequestOnlineApp}
          />
        )}
        {requestingOnlineAppSuccess && (
          <SrSimpleMessageModal
            iconUrl="/images/mail-send-green.svg"
            headline="Request sent!"
            message={
              'We just emailed the agent that manages this home, letting them know ' +
              'you want to apply. Look for an email response from them soon!'
            }
            buttonText="ok thanks"
            maxWidth={375}
            maxIconWidth={200}
            onClose={this._onCloseRequestOnlineAppModals}
          />
        )}
        {isExternalApplicationModalOpen && (
          <SrSimpleMessageModal
            iconUrl="/images/illustration-scooter.svg"
            headline="Leaving Sunroom to Apply on Property Manager’s Site"
            message={
              <div>
                <SrText weight="600">{partner.displayName}</SrText>
                <br />
                <img
                  style={{ width: '100%' }}
                  src={partner.logoUrl}
                  alt={`${partner.displayName} logo`}
                />
                <br />
                <br />
                You will be redirected in 3 seconds.
              </div>
            }
            maxWidth={375}
            maxIconWidth={200}
          />
        )}
        {requestingBuyerSideApp && (
          <SrSimpleMessageModal
            iconUrl="/images/mail-send-green.svg"
            headline="Application Requested!"
            message="A Sunroom application specialist will text you soon with instructions on how to apply."
            buttonText="ok thanks"
            maxWidth={375}
            maxIconWidth={200}
            onClose={this._onCloseRequestBuyerSideAppModal}
          />
        )}
        <ButtonDeprecated
          onClick={this._onButtonClick}
          disabled={buttonDisabled}
          className={classnames(className, {
            started: applicationStarted,
            completed: applicationCompleted,
          })}
          theme={theme}
        >
          {buttonText}
        </ButtonDeprecated>
      </div>
    );
  }
}

SrApplicationButton.displayName = 'SrApplicationButton';

export default SrApplicationButton;
